import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { toast } from 'react-toastify';
import { Button, Col, Form, Input, Row } from 'reactstrap';

import { ScreenPrompt } from 'components/ScreenPrompt';
import { SelectedProductsTable } from '../components/SelectedProductsTable';
import DiscountMatrixTable from '../components/DiscountMatrixTable/';

import productService from 'views/CRM/Product/services/productService';
import priceCatalogService from '../../service/priceCatalogService';
import axios from 'axios';
import UpdatePriceCatalog from '../components/UpdatePriceCatalog';
import { useDiscountsMatrix } from 'hooks/useDiscountsMatrix';

const PriceCatalogInputs = ({ id, priceCatalog }) => {
  const history = useHistory();

  const [productsList, setProductsList] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState(new Set());

  const {
    discounts: globalDiscounts,
    handleValidateDiscounts,
    handleAddDiscount,
    handleRemoveDiscount,
    handleDiscountChange
  } = useDiscountsMatrix([]);

  const {
    discounts: updateDiscountMatrix,
    setDiscounts: setUpdateDiscountMatrix,
    handleValidateDiscounts: handleValidateUpdateDiscountsMatrix
  } = useDiscountsMatrix([]);
  const [updatedProducts, setUpdatedProducts] = useState([]);

  const { control, handleSubmit, formState, errors, reset } = useForm();
  const { isDirty } = formState;

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await productService.getAllProducts();
        setProductsList(response.data.data);
      } catch (error) {
        if (axios.isAxiosError(error)) {
          toast.error('Erro ao carregar produtos');
          console.log(error.message);
        } else {
          toast.error('Erro inesperado ao carregar produtos');
        }
        console.error(error);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    if (priceCatalog) {
      reset({ ...priceCatalog });
    }
  }, [priceCatalog]);

  const hasFieldsChanged = (data, sourceData) => {
    return (
      data.name !== sourceData.name ||
      data.description !== sourceData.description
    );
  };

  const createNewCatalog = async (data, products, globalDiscounts) => {
    const catalog = {
      name: data.name,
      description: data.description,
      products: products.map((product) => ({
        id: product.productId,
        catalogPrice: product.catalogPrice
      })),
      isActive: true
    };

    if (globalDiscounts.length > 0) {
      catalog.discountMatrix = globalDiscounts.map((discount, index) => ({
        minQuantity: discount.minQuantity,
        maxQuantity: discount.maxQuantity,
        discount: discount.discount,
        type: index < globalDiscounts.length - 1 ? 'between' : 'above'
      }));
    }

    return priceCatalogService.createPriceCatalog(catalog);
  };

  const updateExistingCatalog = async (
    id,
    data,
    updatedProducts,
    updateDiscountMatrix
  ) => {
    if (hasFieldsChanged(data, priceCatalog)) {
      await priceCatalogService.updatePriceCatalog(id, {
        name: data.name,
        description: data.description
      });
    }

    for (const product of updatedProducts) {
      try {
        await priceCatalogService.updateProductPriceInCatalog(id, product.id, {
          catalogPrice: product.catalogPrice
        });
      } catch (error) {
        console.error(`Failed to update product ${product.id}:`, error);
        // You might want to collect errors or notify the user
        toast.error(`Failed to update product ${product.id}`);
      }
    }

    await priceCatalogService.updatePriceCatalogDiscountMatrix(
      id,
      updateDiscountMatrix
    );
  };

  const validateDiscountMatrix = (discounts, isUpdate = false) => {
    const validationFn = isUpdate
      ? handleValidateUpdateDiscountsMatrix
      : handleValidateDiscounts;
    if (!validationFn()) {
      throw new Error(
        'Os intervalos de quantidade na matriz de descontos são inválidos.'
      );
    }
  };

  const handleSubmitError = (error) => {
    if (axios.isAxiosError(error)) {
      toast.error(
        error.response?.data?.message || 'Erro ao processar a requisição'
      );
    } else if (error.message) {
      toast.error(error.message);
    } else {
      toast.error('Erro inesperado');
    }
    console.error('Price catalog submission error:', error);
  };

  const onSubmit = async (data) => {
    try {
      if (id) {
        validateDiscountMatrix(updateDiscountMatrix, true);
        await updateExistingCatalog(
          id,
          data,
          updatedProducts,
          updateDiscountMatrix
        );
      } else {
        validateDiscountMatrix(globalDiscounts);
        await createNewCatalog(data, products, globalDiscounts);
      }

      toast.success(
        `Catálogo de preços ${id ? 'atualizado' : 'criado'} com sucesso`
      );

      history.push('/admin/crm/priceCatalog');
    } catch (error) {
      handleSubmitError(error);
    }
  };

  const errorFormMessage = (message) => (
    <p style={{ color: 'red' }}>{message}</p>
  );

  const cancelAction = () => {
    history.push('/admin/crm/priceCatalog');
  };

  return (
    <>
      <ScreenPrompt
        when={isDirty && !formState.isSubmitting}
        message={`Está certo de que deseja sair? Terá que preencher os dados novamente quando retornar`}
      />
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <Col sm="6" className="mb-3">
            <label className="form-control-label text-capitalize">Nome*</label>
            <Controller
              render={(props) => (
                <Input
                  value={props.value}
                  onChange={props.onChange}
                  type="text"
                />
              )}
              control={control}
              defaultValue={priceCatalog?.name || ''}
              rules={{ required: 'Campo obrigatório' }}
              name="name"
            />
            <ErrorMessage
              errors={errors}
              name="name"
              render={({ message }) => errorFormMessage(message)}
            />
          </Col>
          <Col sm="6" className="mb-3">
            <label className="form-control-label text-capitalize">
              Descrição
            </label>
            <Controller
              render={(props) => (
                <Input
                  value={props.value}
                  onChange={props.onChange}
                  type="textarea"
                />
              )}
              control={control}
              defaultValue={priceCatalog?.description || ''}
              rules={{ maxLength: 255, minLength: 5 }}
              name="description"
            />
            <ErrorMessage
              errors={errors}
              name="description"
              render={({ message }) => errorFormMessage(message)}
            />
          </Col>
        </Row>
        {id ? (
          <>
            <UpdatePriceCatalog
              catalogId={id}
              onUpdatedDiscountMatrix={setUpdateDiscountMatrix}
              onUpdatedProducts={setUpdatedProducts}
            />
          </>
        ) : (
          <>
            <SelectedProductsTable
              products={products}
              setProducts={setProducts}
              selectedProducts={selectedProducts}
              setSelectedProducts={setSelectedProducts}
              productsList={productsList}
              control={control}
            />

            <div className="mt-4">
              <h4>Matriz de Desconto</h4>
              <DiscountMatrixTable
                discounts={globalDiscounts}
                onAddDiscount={handleAddDiscount}
                onRemoveDiscount={handleRemoveDiscount}
                onDiscountChange={handleDiscountChange}
              />
            </div>
          </>
        )}
        <Button type="submit" color="primary">
          Salvar
        </Button>
        <Button type="button" color="danger" onClick={cancelAction}>
          Cancelar
        </Button>
      </Form>
    </>
  );
};

export default PriceCatalogInputs;
